import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import apiConfig from "../../../apiConfig";

const GetAllReseller = () => {
  const [resellers, setResellers] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchResellers();
  }, []);

  const fetchResellers = async () => {
    try {
      setLoading(true);
      const res = await axios.get(
        `${apiConfig.baseUrl}${apiConfig.GetAllReseller}`,
      );
      setResellers(res.data.data || []);
    } catch (error) {
      toast.error("Failed to fetch resellers");
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (id, status) => {
    try {
      setLoading(true);

      await axios.put(
        `${apiConfig.baseUrl}${apiConfig.GetResellerUpdateStatus}/${id}`,
        { status },
      );

      toast.success(`Reseller ${status}`);
      fetchResellers();
    } catch (error) {
      toast.error("Action failed");
    } finally {
      setLoading(false);
    }
  };

  const deleteReseller = async (id) => {
    try {
      setLoading(true);

      await axios.delete(
        `${apiConfig.baseUrl}${apiConfig.DeleteReseller}/${id}`,
      );

      toast.success("Reseller deleted");
      setResellers((prev) => prev.filter((r) => r._id !== id));
    } catch (error) {
      toast.error("Delete failed");
    } finally {
      setLoading(false);
    }
  };

  const statusStyle = (status) => {
    if (status === "approved") return "bg-green-100 text-green-700";
    if (status === "rejected") return "bg-red-100 text-red-700";
    return "bg-yellow-100 text-yellow-700";
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen relative">
      {/* 🔥 LOADER */}
      {loading && (
        <div className="absolute inset-0 bg-white/60 flex items-center justify-center z-50">
          <div className="bg-white px-6 py-3 rounded shadow">Loading...</div>
        </div>
      )}

      <h2 className="text-2xl font-bold mb-6">All Resellers</h2>

      {/* ✅ DESKTOP TABLE */}
      <div className="hidden md:block bg-white rounded-xl shadow border overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-gray-100 text-gray-600">
            <tr>
              <th className="p-3 text-left">Name</th>
              <th>Email</th>
              <th>Phone</th>
              <th>Status</th>
              <th>ID</th>
              <th>bankName</th>
              <th>holderName</th>
              <th>ifscCode</th>
              <th>accountNumber</th>
              <th className="text-center">Action</th>
              <th className="text-center">Delete</th>
            </tr>
          </thead>

          <tbody>
            {resellers.length === 0 ? (
              <tr>
                <td colSpan="7" className="text-center p-5">
                  No resellers found
                </td>
              </tr>
            ) : (
              resellers.map((r) => (
                <tr key={r._id} className="border-t hover:bg-gray-50">
                  <td className="p-3 font-medium">{r.fullName}</td>
                  <td>{r.email}</td>
                  <td>{r.phone}</td>

                  <td>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-semibold ${statusStyle(r.status)}`}
                    >
                      {r.status}
                    </span>
                  </td>
                  <td>{r.resellerId}</td>
                  <td>
                    {r.bankDetails?.bankName ? r.bankDetails?.bankName : "-"}
                  </td>
                  <td>
                    {r.bankDetails?.holderName
                      ? r.bankDetails?.holderName
                      : "- "}
                  </td>
                  <td>
                    {r.bankDetails?.ifscCode ? r.bankDetails?.ifscCode : "-"}
                  </td>
                  <td>
                    {r.bankDetails?.accountNumber
                      ? r.bankDetails?.accountNumber
                      : "-"}
                  </td>
                  <td className="text-center">
                    {r.status === "pending" ? (
                      <div className="flex justify-center gap-2">
                        <button
                          onClick={() => updateStatus(r._id, "approved")}
                          className="bg-green-600 hover:bg-green-700 text-white px-3 py-1 rounded text-xs"
                        >
                          Approve
                        </button>

                        <button
                          onClick={() => updateStatus(r._id, "rejected")}
                          className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded text-xs"
                        >
                          Reject
                        </button>
                      </div>
                    ) : ["approved", "Approved", "active"].includes(
                        r.status,
                      ) ? (
                      <span className="text-green-600 font-semibold text-xs">
                        Approved
                      </span>
                    ) : ["rejected", "Rejected"].includes(r.status) ? (
                      <span className="text-red-600 font-semibold text-xs">
                        Rejected
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">{r.status}</span>
                    )}
                  </td>
                  <td className="text-center">
                    <button
                      onClick={() => deleteReseller(r._id)}
                      className="bg-red-600 text-white px-3 py-1 rounded text-xs"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ✅ MOBILE VIEW */}
      <div className="md:hidden space-y-4 mt-4">
        {resellers.map((r) => (
          <div key={r._id} className="bg-white p-4 rounded-xl shadow border">
            <div className="flex justify-between items-center">
              <h3 className="font-semibold">{r.fullName}</h3>

              <span
                className={`px-2 py-1 text-xs rounded ${statusStyle(r.status)}`}
              >
                {r.status}
              </span>
            </div>

            <p className="text-sm text-gray-600 mt-1">{r.email}</p>
            <p className="text-sm">📞 {r.phone}</p>
            <p className="text-xs text-gray-400 mt-1">ID: {r.resellerId}</p>

            {r.status === "pending" && (
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => updateStatus(r._id, "approved")}
                  className="flex-1 bg-green-600 text-white py-2 rounded text-sm"
                >
                  Approve
                </button>

                <button
                  onClick={() => updateStatus(r._id, "rejected")}
                  className="flex-1 bg-red-600 text-white py-2 rounded text-sm"
                >
                  Reject
                </button>
              </div>
            )}

            <button
              onClick={() => deleteReseller(r._id)}
              className="w-full mt-3 bg-red-600 text-white py-2 rounded text-sm"
            >
              Delete
            </button>
          </div>
        ))}
      </div>
    </div>
  );
};

export default GetAllReseller;
